import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, bookingApi, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type {
  BookingEvent,
  BookingMoveTargets,
  EventBooking,
  EventHost,
  EventLocation,
  EventPackage,
  EventServiceType,
  EventTimeSlot,
  PagedTimeSlots,
  SavePackageInput,
} from './types';

const get = <T>(url: string) => unwrap<T>(bookingApi.get<ApiEnvelope<T>>(url));

// ------------------------------------------------------------------- events

export function useEvents() {
  return useQuery({
    queryKey: ['events', 'all'],
    queryFn: () => get<BookingEvent[]>('events/all'),
    staleTime: 60_000,
  });
}

export function useEventServiceTypes(enabled = true) {
  return useQuery({
    queryKey: ['booking', 'service-types'],
    queryFn: () => get<EventServiceType[]>('servicetypes/list'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEventHosts(enabled = true) {
  return useQuery({
    queryKey: ['booking', 'hosts'],
    queryFn: () => get<EventHost[]>('hosts'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEventLocations(enabled = true) {
  return useQuery({
    queryKey: ['booking', 'locations'],
    queryFn: () => get<EventLocation[]>('locations'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateEvents() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['events'] });
}

/** Both create and update are POSTs, to different paths — the original's shape. */
export function useSaveEvent() {
  const invalidate = useInvalidateEvents();

  return useMutation({
    mutationFn: (event: Partial<BookingEvent> & { id?: string }) =>
      unwrap<unknown>(
        bookingApi.post(event.id ? 'events/update' : 'events/create', event)
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteEvent() {
  const invalidate = useInvalidateEvents();

  return useMutation({
    mutationFn: (eventId: string) =>
      unwrap<unknown>(bookingApi.delete(`events/${eventId}`)),
    onSuccess: invalidate,
  });
}

export function useUploadEventImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      // The main API, not the booking one.
      return unwrap<string>(
        api.post('utility/bookingevents/uploadfile', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}

// --------------------------------------------------------------- time slots

export function useTimeSlots({
  eventId,
  type,
  page,
  size,
  enabled,
}: {
  eventId: string | null;
  type: number;
  page: number;
  size: number;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: ['events', eventId, 'slots', type, page, size],
    queryFn: () =>
      get<PagedTimeSlots>(
        `events/timeSlots/v2/${eventId}?page=${page}&size=${size}&type=${type}`
      ),
    enabled: enabled && Boolean(eventId),
  });
}

/**
 * Times are shifted by `VITE_TIMEZONE_OFFSET` on the way in and out.
 *
 * The API stores wall-clock values without a zone; the original added the
 * offset for display and subtracted it before sending. Both directions live
 * here so the components never do the arithmetic.
 */
export function toApiTime(value: Date, offsetHours: number): string {
  const shifted = new Date(value.getTime() - offsetHours * 3_600_000);
  return `${shifted.toISOString().slice(0, 19)}.000`;
}

export function fromApiTime(
  value: string | null | undefined,
  offsetHours: number
): Date | null {
  if (!value) return null;
  const parsed = new Date(/[zZ]$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getTime() + offsetHours * 3_600_000);
}

/** `CreateEventTimeSlotBindingModel`. Field names are camelCase per the spec. */
export interface CreateTimeSlotInput {
  startTime: string;
  endTime: string;
  maxCapacity: number;
  eventId: string;
  isActive: boolean;
  force?: boolean;
  timeSlotHostId?: string | null;
}

/**
 * Creating a slot can come back with a *list of conflicts* instead of an
 * error — `data` is then the clashing slots and the caller re-sends with
 * `Force: true`.
 */
export function useCreateTimeSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTimeSlotInput) =>
      unwrap<EventTimeSlot[] | null>(
        bookingApi.post('events/timeSlots', input)
      ).catch((error: unknown) => {
        // `unwrap` throws on an empty body; a successful create returns none.
        if (error instanceof Error && error.message.includes('empty')) {
          return null;
        }
        throw error;
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useUpdateTimeSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    // `UpdateEventTimeSlotBindingModel` — includes `comment`, which the list
    // returns and the edit form can change.
    mutationFn: (input: {
      timeSlotId: string;
      startTime: string;
      endTime: string;
      maxCapacity: number;
      comment?: string | null;
      isActive: boolean;
      timeSlotHostId?: string | null;
    }) => unwrap<unknown>(bookingApi.put('events/timeSlots', input)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useDeleteTimeSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    // Lowercase `timeslots` on delete, camelCase `timeSlots` on create and
    // update — the spec really is inconsistent here. ASP.NET routing is
    // case-insensitive so both resolve, but this matches what is documented.
    mutationFn: (timeSlotId: string) =>
      unwrap<unknown>(bookingApi.delete(`events/timeslots/${timeSlotId}`)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

// ----------------------------------------------------------------- bookings

export function useTimeSlotBookings(timeSlotId: string | null) {
  return useQuery({
    queryKey: ['events', 'slot', timeSlotId, 'bookings'],
    queryFn: () =>
      get<EventBooking[]>(`events/timeSlots/${timeSlotId}/bookings`),
    enabled: Boolean(timeSlotId),
  });
}

function useInvalidateBookings() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['events'] });
}

/** `BypassPayment` is the inverse of the "create charge" prompt. */
export function useCreateBooking() {
  const invalidate = useInvalidateBookings();

  return useMutation({
    // `CreateBookEventBindingModel`. The admin path delegates to the same
    // `BookEventAsync` the customer flow uses, so a packaged event demands
    // `eventPackageId` here exactly as it does of a customer.
    mutationFn: (input: {
      userId: string;
      eventTimeSlotId: string;
      bypassPayment: boolean;
      comments?: string | null;
      status?: number;
      eventPackageId?: string;
    }) => unwrap<unknown>(bookingApi.post('events/booking', input)),
    onSuccess: invalidate,
  });
}

export function useUpdateBooking() {
  const invalidate = useInvalidateBookings();

  return useMutation({
    // `UpdateBookingBindingModel`.
    mutationFn: (input: {
      bookingId: string;
      status: number;
      comments: string | null;
      additionalInformation1Response: string | null;
      additionalInformation2Response: string | null;
    }) => unwrap<unknown>(bookingApi.put('events/booking', input)),
    onSuccess: invalidate,
  });
}

/** Cancelling a booking is a POST to the booking's own id. */
export function useCancelBooking() {
  const invalidate = useInvalidateBookings();

  return useMutation({
    mutationFn: ({
      bookingId,
      cancellationFee,
    }: {
      bookingId: string;
      cancellationFee: number;
    }) =>
      unwrap<unknown>(
        bookingApi.post(`events/booking/${bookingId}`, {
          cancellationFee,
        })
      ),
    onSuccess: invalidate,
  });
}

export function useCreatePurchase() {
  const invalidate = useInvalidateBookings();

  return useMutation({
    // `CreatePurchaseBindingModel`.
    mutationFn: (input: {
      bookingId: string;
      userId: string;
      amount: number;
    }) => unwrap<unknown>(bookingApi.post('purchases', input)),
    onSuccess: invalidate,
  });
}

export function useCancelPurchase() {
  const invalidate = useInvalidateBookings();

  return useMutation({
    mutationFn: ({
      purchaseId,
      cancellationFee,
    }: {
      purchaseId: string;
      cancellationFee: number;
    }) =>
      unwrap<unknown>(
        bookingApi.post(`purchases/cancel/v2/${purchaseId}`, {
          cancellationFee,
        })
      ),
    onSuccess: invalidate,
  });
}

export function useRefundPurchase() {
  const invalidate = useInvalidateBookings();

  return useMutation({
    // `RefundPurchaseBindingModel`.
    mutationFn: (input: { purchaseId: string; refundAmount: number }) =>
      unwrap<unknown>(bookingApi.post('purchases/refund', input)),
    onSuccess: invalidate,
  });
}

/** User lookup for "add booking" — the main API, same endpoint the CS page uses. */
export function useUserSearch(term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['users', 'search', term],
    queryFn: () =>
      unwrap<{ results: { id: string; firstName: string; lastName: string; email: string }[] }>(
        api.post('users/search', { SearchText: term })
      ),
    enabled: enabled && term.trim().length > 0,
  });
}


// ------------------------------------------------- move booking / payments

/**
 * Slots this booking could be moved into.
 *
 * `data` is a `BookingMoveTargetsDto` — an object describing the booking, with
 * the candidate slots under `timeslots`. Each carries `booked`/`spotsLeft` and
 * an `eligible` flag with a reason, not the `currentCapacity` the slot list
 * uses.
 */
export function useMoveTargets(bookingId: string | null, includePast: boolean) {
  return useQuery({
    queryKey: ['events', 'booking', bookingId, 'move-targets', includePast],
    queryFn: () =>
      get<BookingMoveTargets>(
        `v2/admin/bookings/${bookingId}/move-targets?includePast=${includePast}`
      ),
    enabled: Boolean(bookingId),
  });
}

export function useMoveBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      ...body
    }: {
      bookingId: string;
      targetTimeSlotId: string;
      reason: string;
      increaseTargetCapacity: boolean;
      allowPastTimeSlot: boolean;
      // `MoveBookingModel` also carries `bookingId`; the path has it too.
    }) =>
      unwrap<unknown>(
        bookingApi.post(`v2/admin/bookings/${bookingId}/move`, {
          bookingId,
          ...body,
        })
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export interface RecordPaymentInput {
  amount: number;
  method: number;
  offlineReference?: string;
  adminNotes?: string;
  /** Either apply to an existing purchase, or open a new one. */
  purchaseId?: string;
  createNewPurchase?: boolean;
}

/**
 * Offline payment against a booking or a membership.
 *
 * `RecordBookingOfflinePaymentModel`. The membership route is
 * `v2/admin/memberships/...`, not `subscriptions` — the two names for the same
 * concept differ between the v1 and v2 halves of this API.
 */
export function useRecordPayment(endpoint: 'bookings' | 'memberships') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      targetId,
      ...body
    }: RecordPaymentInput & { targetId: string }) =>
      unwrap<unknown>(
        bookingApi.post(`v2/admin/${endpoint}/${targetId}/payments`, body)
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}


// ----------------------------------------------------------------- packages

/**
 * The event's packages, newest state first from the server's own sort order.
 *
 * `includeInactive` defaults on: the management screen has to show retired
 * packages, because a retired one is still named on old bookings and
 * reactivating is the only way back. Pass `false` to see what a customer
 * would actually be offered.
 */
export function useEventPackages(eventId?: string | null, includeInactive = true) {
  return useQuery({
    queryKey: ['events', eventId, 'packages', includeInactive],
    queryFn: () =>
      get<EventPackage[]>(
        `v2/admin/events/${eventId}/packages?includeInactive=${includeInactive}`
      ),
    enabled: Boolean(eventId),
  });
}

/**
 * Whether this event forces a package choice, and whether we know yet.
 *
 * One active package is enough — the server rejects a booking with no package
 * from that point on. `loading` is reported separately so a creation form can
 * hold its submit closed until the answer is in, rather than letting a booking
 * through in the window before the list arrives.
 */
export function useRequiresPackage(eventId?: string | null) {
  const packages = useEventPackages(eventId, false);

  return {
    required: (packages.data ?? []).length > 0,
    // A disabled query reports pending forever, so an absent event is not
    // "still loading" — it simply has no packages to ask about.
    loading: Boolean(eventId) && packages.isPending,
  };
}

/**
 * Create or update, on the shape of `useSaveEvent` — but note the paths are
 * not parallel: create is nested under the event, update addresses the
 * package on its own.
 */
export function useSavePackage() {
  const invalidate = useInvalidateEvents();

  return useMutation({
    mutationFn: ({ id, eventId, ...body }: SavePackageInput) =>
      unwrap<EventPackage>(
        id
          ? bookingApi.put(`v2/admin/events/packages/${id}`, body)
          : bookingApi.post(`v2/admin/events/${eventId}/packages`, body)
      ),
    onSuccess: invalidate,
  });
}

/**
 * Takes a package off sale. A soft delete — bookings that chose it keep
 * naming it — which is why the UI says "retire", not "delete".
 *
 * Refused when it would leave an event that has live packaged bookings with
 * nothing active left to sell; the message comes back localized.
 */
export function useDeletePackage() {
  const invalidate = useInvalidateEvents();

  return useMutation({
    mutationFn: (packageId: string) =>
      unwrap<unknown>(bookingApi.delete(`v2/admin/events/packages/${packageId}`)),
    onSuccess: invalidate,
  });
}

/**
 * Moves one booking onto a different package of the same event.
 *
 * Financial, not administrative: it re-prices purchases, can settle them and
 * can confirm the booking. The response message says what happened to the
 * money — it is not always "saved" — so callers should surface it rather than
 * a generic success.
 */
export function useChangeBookingPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    // `ChangeBookingPackageModel`.
    mutationFn: async ({
      bookingId,
      ...body
    }: {
      bookingId: string;
      eventPackageId: string;
      reason?: string;
      adjustPurchaseAmount: boolean;
    }) => {
      const response = await bookingApi.put<ApiEnvelope<unknown>>(
        `v2/admin/bookings/${bookingId}/package`,
        body
      );
      // `unwrap` is still the verdict — it throws the localized reason on a
      // `success: false` that arrived as HTTP 200. What it discards is the
      // message on the way through, and here that message is the answer to
      // "did the money move?", so it is read back off the same response.
      await unwrap<unknown>(Promise.resolve(response));
      return response.data?.message ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      void queryClient.invalidateQueries({ queryKey: ['manage-bookings'] });
    },
  });
}
